
import { supabase } from '@/integrations/supabase/client';
import type { EventInsert, EventUpdate } from '@/integrations/supabase/client';

/**
 * Helper function to clean up all user data before deleting a user.
 * This ensures all traces of the user are removed from the database.
 * 
 * @param userId The UUID of the user to be deleted
 * @returns A promise that resolves when the operation is complete
 */
export const completeUserDeletion = async (userId: string): Promise<void> => {
  try {
    console.log(`Initiating complete deletion for user: ${userId}`);
    
    // Ensure userId is properly formatted as a string
    if (!userId || typeof userId !== 'string' || !userId.trim()) {
      throw new Error('Invalid user ID provided');
    }
    
    // Our database function now properly handles the conversion from string to UUID
    const { error } = await supabase.rpc('admin_delete_user', {
      p_user_id: userId
    });
    
    if (error) {
      console.error('Error in admin_delete_user RPC:', error);
      throw error;
    }
    
    console.log('User and all associated data successfully deleted');
  } catch (error) {
    console.error('Error in completeUserDeletion helper:', error);
    throw error;
  }
};

/**
 * Temporary wrapper functions to handle event operations while avoiding RLS issues
 * For creating events, we use direct table access
 */
export const temporaryCreateEvent = async (eventData: EventInsert) => {
  try {
    console.log('Creating event with data:', eventData);
    
    // Use RPC function to create event and bypass type issues
    const { data, error } = await supabase.rpc('admin_create_event', {
      p_title: eventData.title,
      p_event_type: eventData.event_type,
      p_custom_type: eventData.custom_type || null,
      p_start_time: eventData.start_time,
      p_end_time: eventData.end_time,
      p_comments: eventData.comments || null,
      p_created_by: eventData.created_by
    });
    
    if (error) {
      console.error('Error in RPC admin_create_event:', error);
      // If the RPC fails, this is now just for logging - we won't use direct table insertion
      console.error('RPC failure, cannot proceed with event creation');
      return { data: null, error };
    }
    
    return { data, error: null };
    
  } catch (error) {
    console.error('Error in temporaryCreateEvent:', error);
    return { data: null, error };
  }
};

/**
 * Temporary wrapper for updating events
 */
export const temporaryUpdateEvent = async (id: string, eventData: EventUpdate) => {
  try {
    console.log(`Updating event ${id} with data:`, eventData);
    
    // Use RPC function to update event and bypass type issues
    const { data, error } = await supabase.rpc('admin_update_event', {
      p_id: id,
      p_title: eventData.title || '',
      p_event_type: eventData.event_type || '',
      p_custom_type: eventData.custom_type || null,
      p_start_time: eventData.start_time || new Date().toISOString(),
      p_end_time: eventData.end_time || new Date().toISOString(),
      p_comments: eventData.comments || null
    });
    
    if (error) {
      console.error('Error in RPC admin_update_event:', error);
      // If the RPC fails, this is now just for logging - we won't use direct table update
      console.error('RPC failure, cannot proceed with event update');
      return { data: null, error };
    }
    
    return { data, error: null };
    
  } catch (error) {
    console.error('Error in temporaryUpdateEvent:', error);
    return { data: null, error };
  }
};

/**
 * Temporary wrapper for deleting events
 */
export const temporaryDeleteEvent = async (id: string) => {
  try {
    console.log(`Deleting event with ID: ${id}`);
    
    // Use RPC function to delete event and bypass type issues
    const { data, error } = await supabase.rpc('admin_delete_event', {
      p_id: id
    });
    
    if (error) {
      console.error('Error in RPC admin_delete_event:', error);
      // If the RPC fails, this is now just for logging - we won't use direct table deletion
      console.error('RPC failure, cannot proceed with event deletion');
      return { data: null, error };
    }
    
    return { data, error: null };
    
  } catch (error) {
    console.error('Error in temporaryDeleteEvent:', error);
    return { data: null, error };
  }
};
